import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Event, EventFilters, eventsService } from '../services/events';
import { SeverityBadge, StatusBadge, TypeBadge } from '../components/EventBadges';
import EventDetailsModal from '../components/EventDetailsModal';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/i18nFormat';
import PageHeader from '../components/UI/PageHeader';
import { downloadStixBundle } from '../services/taxii';

const Events: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 0,
    limit: 20
  });
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [phishingStats, setPhishingStats] = useState<any>(null);

  const [exportingStix, setExportingStix] = useState(false);

  const handleExportStix = async () => {
    setExportingStix(true);
    try {
      await downloadStixBundle();
      toast.success('Bundle STIX 2.1 baixado com sucesso');
    } catch {
      toast.error('Erro ao exportar IOCs');
    } finally {
      setExportingStix(false);
    }
  };

  // Filtros
  const [filters, setFilters] = useState<EventFilters>({
    skip: 0,
    limit: 20,
    search: '',
    severity: '',
    status: '',
    event_type: '',
  });

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Carregar eventos
  const loadEvents = useCallback(
    async (resetPagination = false, customFilters?: EventFilters) => {
      try {
        setLoading(true);

        const filtersToUse = customFilters || filters;
        const currentFilters = resetPagination ? { ...filtersToUse, skip: 0 } : filtersToUse;

        if (resetPagination) {
          setCurrentPage(1);
        }

        const data = await eventsService.getEvents(currentFilters);
        setEvents(data);

        // When searching, we get all matching results, so disable pagination
        if (currentFilters.search && currentFilters.search.trim() !== '') {
          setHasMore(false);
        } else {
          setHasMore(data.length === (filtersToUse.limit || 20));
        }
      } catch (error) {
        console.error('Error loading events:', error);
        toast.error(t('events.error.loading'));
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      if ((filters.event_type || '') === 'phishing') {
        const pStats = await eventsService.getPhishingStats();
        setPhishingStats(pStats);
        setStats(null);
      } else {
        const statsData = await eventsService.getEventStats();
        setStats(statsData);
        setPhishingStats(null);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [filters.event_type]);

  // Efeitos
  useEffect(() => {
    loadEvents();
    loadStats();
  }, [loadEvents, loadStats]);

  useEffect(() => {
    loadStats();
  }, [filters.event_type, loadStats]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchFilters = {
        ...filters,
        skip: 0, // Reset pagination when filters change
      };
      setCurrentPage(1);
      loadEvents(false, searchFilters);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.severity, filters.status, filters.event_type]);

  // Handlers
  const handleFilterChange = (key: keyof EventFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePageChange = (newPage: number) => {
    const newSkip = (newPage - 1) * (filters.limit || 20);
    const newFilters = { ...filters, skip: newSkip };
    setFilters(newFilters);
    setCurrentPage(newPage);
    loadEvents(false, newFilters);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleEventUpdated = () => {
    loadEvents();
    loadStats();
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t('events.relative.justNow');
    if (diffInHours < 24) return t('events.relative.hoursAgo', { count: diffInHours });
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('events.relative.daysAgo', { count: diffInDays });
    return formatDate(dateString);
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      {/* Header */}
      <PageHeader
        title={t('events.title')}
        subtitle={t('events.subtitle')}
      >
        <button
          onClick={handleExportStix}
          disabled={exportingStix}
          className='inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50'
        >
          <ArrowDownTrayIcon className='h-4 w-4' />
          {exportingStix ? 'Exportando…' : 'Exportar IOCs (STIX 2.1)'}
        </button>
      </PageHeader>

      {/* Estatísticas */}
      {stats && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <InformationCircleIcon className='h-6 w-6 text-gray-400 dark:text-gray-500' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.totalEvents')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ExclamationTriangleIcon className='h-6 w-6 text-red-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.critical')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                      {stats.by_severity.critical || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ExclamationTriangleIcon className='h-6 w-6 text-orange-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.highSeverity')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                      {stats.by_severity.high || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <InformationCircleIcon className='h-6 w-6 text-blue-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.new')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>{stats.by_status.new || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {phishingStats && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <InformationCircleIcon className='h-6 w-6 text-blue-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.total')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>{phishingStats.total_events || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ExclamationTriangleIcon className='h-6 w-6 text-orange-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.highSeverity')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>{phishingStats.high_risk_events || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ExclamationTriangleIcon className='h-6 w-6 text-yellow-400' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>{t('events.stats.mediumSeverity')}</dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-gray-100'>{phishingStats.medium_risk_events || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='md:col-span-3 bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>{t('events.stats.topTargetDomains')}</h4>
              <div className='flex flex-wrap gap-3'>
                {(phishingStats.top_target_domains || []).map((item: any) => (
                  <span key={item.domain} className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600'>
                    {item.domain} · {item.count}
                  </span>
                ))}
                {(!phishingStats.top_target_domains || phishingStats.top_target_domains.length === 0) && (
                  <span className='text-sm text-gray-500 dark:text-gray-400'>{t('events.empty.noTopDomains')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className='bg-white dark:bg-gray-800 shadow rounded-lg p-6'>
        <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
          {/* Busca */}
          <div className='md:col-span-2'>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400 dark:text-gray-500' />
              </div>
              <input
                type='text'
                placeholder={t('events.filters.searchPlaceholder')}
                value={filters.search || ''}
                onChange={e => handleFilterChange('search', e.target.value)}
                className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
              />
            </div>
          </div>

          {/* Severidade */}
          <div className="relative">
            <select
              value={filters.severity || ''}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className='appearance-none block w-full px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm'
            >
              <option value=''>{t('events.filters.allSeverities')}</option>
              <option value='critical'>{t('events.filters.severity.critical')}</option>
              <option value='high'>{t('events.filters.severity.high')}</option>
              <option value='medium'>{t('events.filters.severity.medium')}</option>
              <option value='low'>{t('events.filters.severity.low')}</option>
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Status */}
          <div className="relative">
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className='appearance-none block w-full px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm'
            >
              <option value=''>{t('events.filters.allStatuses')}</option>
              <option value='new'>{t('events.filters.status.new')}</option>
              <option value='processing'>{t('events.filters.status.processing')}</option>
              <option value='analyzed'>{t('events.filters.status.analyzed')}</option>
              <option value='validated'>{t('events.filters.status.validated')}</option>
              <option value='false_positive'>{t('events.filters.status.falsePositive')}</option>
              <option value='resolved'>{t('events.filters.status.resolved')}</option>
              <option value='ignored'>{t('events.filters.status.ignored')}</option>
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Tipo */}
          <div className="relative">
            <select
              value={filters.event_type || ''}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              className='appearance-none block w-full px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm'
            >
              <option value=''>{t('events.filters.allTypes')}</option>
              <option value='phishing'>{t('events.filters.types.phishing')}</option>
              <option value='malware'>{t('events.filters.types.malware')}</option>
              <option value='data_leak'>{t('events.filters.types.dataLeak')}</option>
              <option value='brand_abuse'>{t('events.filters.types.brandAbuse')}</option>
              <option value='social_media'>{t('events.filters.types.socialMedia')}</option>
              <option value='dark_web'>{t('events.filters.types.darkWeb')}</option>
              <option value='vulnerability'>{t('events.filters.types.vulnerability')}</option>
              <option value='suspicious_domain'>{t('events.filters.types.suspiciousDomain')}</option>
              <option value='credential_leak'>{t('events.filters.types.credentialsLeak')}</option>
              <option value='ioc'>{t('events.filters.types.ioc')}</option>
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className='mt-4 flex justify-between items-center'>
          <button
            onClick={() => loadEvents(true)}
            disabled={loading}
            className='inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50'
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('events.actions.refresh')}
          </button>
        </div>
      </div>

      {/* Lista de Eventos */}
      <div className='bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md'>
        {loading ? (
          <div className='p-8 text-center'>
            <ArrowPathIcon className='h-8 w-8 animate-spin mx-auto text-gray-400 dark:text-gray-500' />
            <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>{t('events.loading')}</p>
          </div>
        ) : events.length === 0 ? (
          <div className='p-8 text-center'>
            <InformationCircleIcon className='h-12 w-12 mx-auto text-gray-400 dark:text-gray-500' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-gray-100'>{t('events.empty.title')}</h3>
            <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
              {t('events.empty.subtitle')}
            </p>
          </div>
        ) : (
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {events.map(event => (
              <li key={event.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                <div className='px-4 py-4 sm:px-6'>
                  <div className='flex items-center justify-between'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center space-x-3 mb-2'>
                        <h3 className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>{event.title}</h3>
                        <SeverityBadge severity={event.severity} size='sm' />
                        <StatusBadge status={event.status} size='sm' />
                        <TypeBadge type={event.event_type} size='sm' />
                      </div>

                      {event.description && (
                        <p className='text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-2'>
                          {event.description}
                        </p>
                      )}

                      <div className='flex items-center text-xs text-gray-500 dark:text-gray-400 space-x-4'>
                        <span>{t('events.card.source')} {event.source}</span>
                        <span>{t('events.card.confidence')} {(event.confidence_score * 100).toFixed(0)}%</span>
                        <span>{t('events.card.risk')} {(event.risk_score * 100).toFixed(0)}%</span>
                        <span>{getRelativeTime(event.created_at)}</span>
                        {event.assigned_to && (
                          <span className='inline-flex items-center space-x-1'>
                            <span>{t('events.card.handledBy')}</span>
                            <span className='font-medium text-blue-600'>{event.assigned_to.name}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {event.source_url && (
                        <a
                          href={event.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t('events.card.viewSource')}
                          className="inline-flex items-center px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">{t('events.card.viewSource')}</span>
                        </a>
                      )}
                      <button
                        onClick={() => handleEventClick(event)}
                        className='inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                      >
                        <EyeIcon className='h-4 w-4' />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Paginação */}
      {!loading && events.length > 0 && !filters.search && (
        <div className='bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow'>
          <div className='flex-1 flex justify-between sm:hidden'>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className='relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {t('events.pagination.prev')}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore}
              className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {t('events.pagination.next')}
            </button>
          </div>

          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-gray-700'>
                {t('events.pagination.page')} <span className='font-medium'>{currentPage}</span> {t('events.pagination.ofEvents')}
              </p>
            </div>
            <div>
              <nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px' aria-label='Pagination'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600'
                >
                  <ChevronLeftIcon className='h-5 w-5' />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasMore}
                  className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600'
                >
                  <ChevronRightIcon className='h-5 w-5' />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Indicador de resultados de busca */}
      {!loading && filters.search && filters.search.trim() !== '' && (
        <div className='bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-lg shadow border border-blue-200 dark:border-blue-800'>
          <div className='flex items-center'>
            <InformationCircleIcon className='h-5 w-5 text-blue-400 mr-2' />
            <p className='text-sm text-blue-700 dark:text-blue-300'>
              {t('events.pagination.showing')} {events.length} {t('events.pagination.resultsFor')} "{filters.search}"
              {events.length === 0 && ` - ${t('events.empty.search')}`}
            </p>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEventUpdated={handleEventUpdated}
      />
    </div>
  );
};

export default Events;
